import { i18n, t } from '../i18n/translations';
import { authService } from '../auth/AuthService';
import { getDemoRestrictionMessage, isDemoMode } from '../utils/demoMode';

declare global {
  interface Window {
    Coloris?: any;
    setBgMode?: (mode: 'transparent' | 'solid' | 'gradient') => void;
    getBgMode?: () => string;
  }
}

function updateSliderVisualFill(slider: HTMLInputElement): void {
  const value = parseFloat(slider.value);
  const min = parseFloat(slider.min);
  const max = parseFloat(slider.max);
  const percentage = max === min ? 0 : ((value - min) / (max - min)) * 100;
  slider.style.setProperty('--slider-value', percentage + '%');
}

export function initializeUI(): void {
  const demoMode = isDemoMode();
  if (window.Coloris) {
    window.Coloris({
      themeMode: 'dark',
      formatToggle: true,
      alpha: false,
      margin: 8,
      swatches: [
        'DarkSlateGray',
        '#2a9d8f',
        '#e9c46a',
        'coral',
        'rgb(231, 111, 81)',
        'Crimson',
        '#023e8a',
        '#0077b6',
        'hsl(194, 100%, 39%)',
        '#00b4d8',
        '#48cae4'
      ],
      onChange: (_color: string, inputEl: HTMLElement | null) => {
        if (inputEl && (inputEl.id === 'bg-color' || inputEl.id === 'bg-color-top' || inputEl.id === 'bg-color-bottom')) {
          inputEl.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    });
  }

  document.getElementById('u-color')?.addEventListener('click', () => {
    setTimeout(() => {
      const picker = document.getElementById('clr-picker');
      if (picker) {
        picker.classList.remove('clr-pos-bg');
        picker.classList.add('clr-pos-material');
      }
    }, 10);
  });

  document.getElementById('bg-color')?.addEventListener('click', () => {
    setTimeout(() => {
      const picker = document.getElementById('clr-picker');
      if (picker) {
        picker.classList.remove('clr-pos-material');
        picker.classList.add('clr-pos-bg');
      }
    }, 10);
  });

  document.getElementById('bg-color-top')?.addEventListener('click', () => {
    setTimeout(() => {
      const picker = document.getElementById('clr-picker');
      if (picker) {
        picker.classList.remove('clr-pos-material');
        picker.classList.add('clr-pos-bg');
      }
    }, 10);
  });

  document.getElementById('bg-color-bottom')?.addEventListener('click', () => {
    setTimeout(() => {
      const picker = document.getElementById('clr-picker');
      if (picker) {
        picker.classList.remove('clr-pos-material');
        picker.classList.add('clr-pos-bg');
      }
    }, 10);
  });

  let bgMode: 'transparent' | 'solid' | 'gradient' = 'transparent';
  const bgModeTransparent = document.getElementById('bg-mode-transparent');
  const bgModeSolid = document.getElementById('bg-mode-solid');
  const bgModeGradient = document.getElementById('bg-mode-gradient');
  const bgSolidControls = document.getElementById('bg-solid-controls');
  const bgGradientControls = document.getElementById('bg-gradient-controls');
  const bgColor = document.getElementById('bg-color') as HTMLInputElement;
  const bgColorTop = document.getElementById('bg-color-top') as HTMLInputElement;
  const bgColorBottom = document.getElementById('bg-color-bottom') as HTMLInputElement;

  const setBgMode = (mode: 'transparent' | 'solid' | 'gradient'): void => {
    bgMode = mode;

    [bgModeTransparent, bgModeSolid, bgModeGradient].forEach(btn => btn?.classList.remove('active'));
    if (mode === 'transparent') bgModeTransparent?.classList.add('active');
    else if (mode === 'solid') bgModeSolid?.classList.add('active');
    else if (mode === 'gradient') bgModeGradient?.classList.add('active');

    bgSolidControls?.classList.toggle('hidden', mode !== 'solid');
    bgGradientControls?.classList.toggle('hidden', mode !== 'gradient');

    applyBackground();
  };

  const applyBackground = (): void => {
    if (bgMode === 'transparent') {
      window.dispatchEvent(new CustomEvent('backgroundChange', { detail: 'transparent' }));
    } else if (bgMode === 'solid') {
      window.dispatchEvent(new CustomEvent('backgroundChange', { detail: bgColor?.value || '#1e1e1e' }));
    } else if (bgMode === 'gradient') {
      window.dispatchEvent(new CustomEvent('backgroundGradient', {
        detail: { top: bgColorTop?.value || '#2a2a2a', bottom: bgColorBottom?.value || '#0a0a0a' }
      }));
    }
  };

  bgModeTransparent?.addEventListener('click', () => setBgMode('transparent'));
  bgModeSolid?.addEventListener('click', () => setBgMode('solid'));
  bgModeGradient?.addEventListener('click', () => setBgMode('gradient'));

  bgColor?.addEventListener('input', () => { if (bgMode === 'solid') applyBackground(); });
  bgColorTop?.addEventListener('input', () => { if (bgMode === 'gradient') applyBackground(); });
  bgColorBottom?.addEventListener('input', () => { if (bgMode === 'gradient') applyBackground(); });

  window.addEventListener('DOMContentLoaded', () => {
    window.dispatchEvent(new CustomEvent('backgroundChange', { detail: '#1a1a1a' }));
  });

  window.setBgMode = setBgMode;
  window.getBgMode = () => bgMode;

  const materialPreview = document.getElementById('material-preview');
  const materialPanel = document.getElementById('material-selector-panel');
  const closeMaterialBtn = document.getElementById('close-material-selector');

  let isMaterialPanelOpen = false;

  materialPreview?.addEventListener('click', () => {
    if (isMaterialPanelOpen) {
      materialPanel?.classList.add('hidden');
      isMaterialPanelOpen = false;
    } else {
      materialPanel?.classList.remove('hidden');
      isMaterialPanelOpen = true;
      // Диспатчим событие открытия панели для lazy loading
      window.dispatchEvent(new CustomEvent('materialPanelOpened'));
    }
  });

  closeMaterialBtn?.addEventListener('click', () => {
    materialPanel?.classList.add('hidden');
    isMaterialPanelOpen = false;
  });

  document.getElementById('add-design-btn')?.addEventListener('click', () => {
    window.dispatchEvent(new CustomEvent('openGraphicEditor'));
  });

  const fileBtn = document.getElementById('file-btn');
  const fileMenu = document.getElementById('file-menu');
  let closeMenuTimeout: ReturnType<typeof setTimeout> | null = null;
  let openSubmenuTimeout: ReturnType<typeof setTimeout> | null = null;

  // Role-based File menu:
  // - non-admin: New project / Open / Save / Settings (no submenus), no model upload
  // - admin: keep current menu with Open submenu (Project/Model)
  const openSubmenuItem = fileMenu?.querySelector('.fabrica-file-submenu-item[data-submenu="open"]') as HTMLElement | null;
  const importModelItem = fileMenu?.querySelector('[data-action="import-model"]') as HTMLElement | null;
  let openProjectSimpleItem: HTMLElement | null = null;
  if (fileMenu && openSubmenuItem) {
    openProjectSimpleItem = document.createElement('div');
    openProjectSimpleItem.className = 'fabrica-dropdown-item hidden';
    openProjectSimpleItem.setAttribute('data-action', 'import-project');
    openProjectSimpleItem.setAttribute('data-i18n', 'menu.open');
    openProjectSimpleItem.textContent = t('menu.open');
    openSubmenuItem.insertAdjacentElement('afterend', openProjectSimpleItem);
  }

  const applyFileMenuByRole = (): void => {
    const isAdmin = authService.isAdmin();
    openSubmenuItem?.classList.toggle('hidden', !isAdmin);
    openProjectSimpleItem?.classList.toggle('hidden', isAdmin);
    importModelItem?.classList.toggle('hidden', !isAdmin);

    // If we just switched to non-admin, close any open submenus immediately.
    if (!isAdmin) {
      document.querySelectorAll('.fabrica-submenu').forEach(submenu => submenu.classList.add('hidden'));
    }
  };
  applyFileMenuByRole();
  authService.onAuthChange(() => {
    applyFileMenuByRole();
    // Ensure translated label for the injected "Open" item matches current language.
    i18n.updateAllElements();
  });

  const cancelCloseMenu = (): void => {
    if (closeMenuTimeout) {
      clearTimeout(closeMenuTimeout);
      closeMenuTimeout = null;
    }
  };

  const scheduleCloseMenu = (): void => {
    cancelCloseMenu();
    closeMenuTimeout = setTimeout(() => {
      if (fileMenu && !fileMenu.matches(':hover') &&
        !fileBtn?.matches(':hover') &&
        !document.querySelector('.fabrica-submenu:hover')) {
        fileMenu.classList.add('hidden');
        document.querySelectorAll('.fabrica-submenu').forEach(submenu => {
          submenu.classList.add('hidden');
        });
      }
    }, 300);
  };

  fileBtn?.addEventListener('mouseenter', () => {
    cancelCloseMenu();
    if (fileMenu) {
      fileMenu.classList.remove('hidden');
    }
  });

  fileBtn?.addEventListener('mouseleave', () => {
    scheduleCloseMenu();
  });

  if (fileMenu) {
    fileMenu.addEventListener('mouseenter', () => {
      cancelCloseMenu();
    });

    fileMenu.addEventListener('mouseleave', (e: Event) => {
      const relatedTarget = (e as MouseEvent).relatedTarget as HTMLElement;
      if (relatedTarget && (
        relatedTarget.closest('.fabrica-submenu') ||
        relatedTarget.closest('.fabrica-file-submenu-item')
      )) {
        return;
      }
      scheduleCloseMenu();
    });
  }

  document.querySelectorAll('.fabrica-file-submenu-item').forEach(item => {
    item.addEventListener('mouseenter', () => {
      if (openSubmenuTimeout) {
        clearTimeout(openSubmenuTimeout);
      }
      openSubmenuTimeout = setTimeout(() => {
        const submenu = item.querySelector('.fabrica-submenu');
        if (submenu) {
          submenu.classList.remove('hidden');
        }
      }, 150);
    });

    item.addEventListener('mouseleave', (e: Event) => {
      const relatedTarget = (e as MouseEvent).relatedTarget as HTMLElement;
      const submenu = item.querySelector('.fabrica-submenu');

      if (relatedTarget && relatedTarget.closest('.fabrica-submenu') === submenu) {
        return;
      }

      setTimeout(() => {
        if (submenu && !submenu.matches(':hover')) {
          submenu.classList.add('hidden');
        }
      }, 200);
    });
  });

  document.querySelectorAll('.fabrica-submenu').forEach(submenu => {
    submenu.addEventListener('mouseenter', () => {
      cancelCloseMenu();
    });

    submenu.addEventListener('mouseleave', (e: Event) => {
      const relatedTarget = (e as MouseEvent).relatedTarget as HTMLElement;

      if (relatedTarget && relatedTarget.closest('.fabrica-file-submenu-item')) {
        return;
      }
      setTimeout(() => {
        if (!submenu.matches(':hover')) {
          submenu.classList.add('hidden');
        }
      }, 200);
    });
  });

  document.querySelectorAll('#file-menu .fabrica-dropdown-item').forEach(item => {
    item.addEventListener('click', () => {
      fileMenu?.classList.add('hidden');
      document.querySelectorAll('.fabrica-submenu').forEach(submenu => {
        submenu.classList.add('hidden');
      });
      const action = item.getAttribute('data-action');
      if (action === 'new-project') {
        if (demoMode) {
          window.dispatchEvent(new CustomEvent('showToolbarMessage', {
            detail: { text: getDemoRestrictionMessage('new-project'), duration: 2500 }
          }));
          return;
        }
        window.open('/', '_blank', 'noopener,noreferrer');
      } else if (action === 'import-model') {
        if (demoMode) {
          window.dispatchEvent(new CustomEvent('showToolbarMessage', {
            detail: { text: getDemoRestrictionMessage('load-model'), duration: 2500 }
          }));
          return;
        }
        if (!authService.isAdmin()) {
          window.dispatchEvent(new CustomEvent('showToolbarMessage', {
            detail: { text: 'Загрузить модель может только администратор', duration: 2500 }
          }));
          return;
        }
        document.getElementById('model-upload')?.click();
      } else if (action === 'import-project') {
        if (demoMode) {
          window.dispatchEvent(new CustomEvent('showToolbarMessage', {
            detail: { text: getDemoRestrictionMessage('load-project'), duration: 2500 }
          }));
          return;
        }
        const projectUpload = document.getElementById('project-upload') as HTMLInputElement;
        if (projectUpload) {
          projectUpload.onchange = (e: Event) => {
            const target = e.target as HTMLInputElement;
            if (target && target.files && target.files.length > 0) {
              const file = target.files[0];
              window.dispatchEvent(new CustomEvent('loadProject', { detail: file }));
            }
          };
          projectUpload.click();
        }
      } else if (action === 'save-project') {
        if (demoMode) {
          window.dispatchEvent(new CustomEvent('showToolbarMessage', {
            detail: { text: getDemoRestrictionMessage('save-project'), duration: 2500 }
          }));
          return;
        }
        window.dispatchEvent(new CustomEvent('saveProject'));
      } else if (action === 'save-screenshot') {
        window.dispatchEvent(new CustomEvent('takeScreenshot'));
      } else if (action === 'save-video') {
        window.dispatchEvent(new CustomEvent('takeVideo'));
      } else if (action === 'show-settings-panel') {
        window.dispatchEvent(new CustomEvent('showSettingsPanel', { detail: { tab: 'general' } }));
      }
    });
  });

  // ===== Settings panel (small, draggable) =====
  const settingsPanel = document.getElementById('settings-panel');
  const settingsClose = document.getElementById('settings-panel-close');
  const navItems = Array.from(document.querySelectorAll('#settings-panel .fabrica-settings-nav-item')) as HTMLButtonElement[];
  const pages = Array.from(document.querySelectorAll('#settings-panel .fabrica-settings-page')) as HTMLElement[];
  const effectsHost = document.getElementById('settings-effects-host');
  const infoHost = document.getElementById('settings-info-host');
  const debugPanel = document.getElementById('debug-panel');
  const infoPanel = document.getElementById('info-panel');

  const quickAngles = document.getElementById('quick-angles-panel');
  const simplifiedToggle = document.getElementById('simplified-ui-toggle') as HTMLInputElement | null;
  const languageSelect = document.getElementById('settings-language-select') as HTMLSelectElement | null;

  const positionUnderFileButton = () => {
    if (!settingsPanel) return;
    const fileBtn = document.getElementById('file-btn');
    if (!fileBtn) return;
    const rect = fileBtn.getBoundingClientRect();
    settingsPanel.style.transform = 'none';
    settingsPanel.style.left = `${Math.max(8, rect.left)}px`;
    settingsPanel.style.top = `${Math.max(8, rect.bottom + 8)}px`;
    settingsPanel.style.right = 'auto';
  };

  const embedEffectsAndInfo = () => {
    if (effectsHost && debugPanel) {
      effectsHost.appendChild(debugPanel);
    }
    if (infoHost && infoPanel) {
      infoHost.appendChild(infoPanel);
    }
  };

  const setSettingsTab = (tab: 'general' | 'hotkeys' | 'optimization' | 'effects' | 'info') => {
    navItems.forEach(btn => btn.classList.toggle('active', btn.getAttribute('data-settings-tab') === tab));
    pages.forEach(p => p.classList.toggle('hidden', p.getAttribute('data-settings-page') !== tab));

    // Only show embedded panels when their tab is active
    if (tab === 'effects') {
      embedEffectsAndInfo();
      debugPanel?.classList.remove('hidden');
    } else {
      debugPanel?.classList.add('hidden');
    }

    if (tab === 'info') {
      embedEffectsAndInfo();
      infoPanel?.classList.remove('hidden');
      window.dispatchEvent(new CustomEvent('showInfoModal'));
    } else {
      infoPanel?.classList.add('hidden');
      window.dispatchEvent(new CustomEvent('hideInfoModal'));
    }
  };

  navItems.forEach(btn => {
    btn.addEventListener('click', () => {
      const tab = (btn.getAttribute('data-settings-tab') || 'general') as any;
      if (tab === 'general' || tab === 'hotkeys' || tab === 'optimization' || tab === 'effects' || tab === 'info') {
        setSettingsTab(tab);
      }
    });
  });

  // Open settings panel (optionally with a specific tab)
  window.addEventListener('showSettingsPanel', ((e: Event) => {
    const detail = (e as CustomEvent).detail as any;
    const tab = (detail?.tab || detail?.section || 'general') as string;
    positionUnderFileButton();
    settingsPanel?.classList.remove('hidden');
    if (tab === 'optimization' || tab === 'hotkeys' || tab === 'effects' || tab === 'info' || tab === 'general') {
      setSettingsTab(tab);
    } else {
      setSettingsTab('general');
    }
  }) as EventListener);

  const closeSettings = () => {
    settingsPanel?.classList.add('hidden');
    debugPanel?.classList.add('hidden');
    infoPanel?.classList.add('hidden');
    window.dispatchEvent(new CustomEvent('hideInfoModal'));
  };

  settingsClose?.addEventListener('click', (e) => {
    e.preventDefault();
    closeSettings();
  });

  // Help button - opens settings panel with hotkeys tab
  const helpButton = document.getElementById('help-button');
  helpButton?.addEventListener('click', () => {
    window.dispatchEvent(new CustomEvent('showSettingsPanel', { detail: { tab: 'hotkeys' } }));
  });

  // Tutorial link - placeholder for now
  const tutorialLink = document.getElementById('tutorial-link');
  tutorialLink?.addEventListener('click', (e) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('showTrainingPanel'));
  });

  // ===== Training panel (PiP-style) =====
  const trainingPanel = document.getElementById('training-panel') as HTMLElement | null;
  const trainingCloseBtn = document.getElementById('training-panel-close') as HTMLButtonElement | null;
  const trainingBackBtn = document.getElementById('training-panel-back') as HTMLButtonElement | null;
  const trainingGrid = document.getElementById('training-panel-grid') as HTMLElement | null;
  const trainingPlayer = document.getElementById('training-panel-player') as HTMLElement | null;
  const trainingIframe = document.getElementById('training-panel-iframe') as HTMLIFrameElement | null;
  const trainingVideoLoading = document.getElementById('training-panel-video-loading') as HTMLElement | null;
  const trainingResizeHandle = document.getElementById('training-panel-resize-handle') as HTMLElement | null;

  type TrainingLesson = {
    id: string;
    title: string;
    duration?: string;
    thumbnailUrl: string;
    embedUrl: string;
  };

  /** Базовый путь к обложкам уроков (WebP в public/training/) */
  const TRAINING_COVERS_BASE = '/training';

  // NOTE: embedUrl — ссылка на видео (Kinescope/YouTube и т.д.). Обложки: public/training/{id}.webp
  const TRAINING_LESSONS: TrainingLesson[] = [
    {
      id: 'intro',
      title: 'Знакомство с интерфейсом',
      duration: '2:40',
      thumbnailUrl: `${TRAINING_COVERS_BASE}/intro.webp`,
      embedUrl: 'https://kinescope.io/embed/iYTcuxsJuUj3FQcAsn6pUe'
    },
    {
      id: 'materials',
      title: 'Материалы: цвет, размер, рельеф',
      duration: '4:15',
      thumbnailUrl: `${TRAINING_COVERS_BASE}/materials.webp`,
      embedUrl: 'https://kinescope.io/embed/rkk3V5K1f2eM8s6pXAkHoq'
    },
    {
      id: 'prints',
      title: 'Принты: добавить и редактировать',
      duration: '5:05',
      thumbnailUrl: `${TRAINING_COVERS_BASE}/prints.webp`,
      embedUrl: 'https://kinescope.io/embed/7TxUDMJRAPL1Tmv3Gp4LAG'
    },
    {
      id: 'export',
      title: 'Экспорт фото и видео',
      duration: '3:20',
      thumbnailUrl: `${TRAINING_COVERS_BASE}/export.webp`,
      embedUrl: 'https://kinescope.io/embed/0e5HMdakVKEVYAQ4ert4ax'
    }
  ];

  const TRAINING_STORAGE_KEY = 'fabrica.trainingPanel';
  const TRAINING_ASPECT = 16 / 9;
  const TRAINING_MIN_W = 320;
  const TRAINING_MIN_H = Math.round(TRAINING_MIN_W / TRAINING_ASPECT); /* 180 for 16/9 */
  const TRAINING_MARGIN = 12;

  const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

  const getViewportBounds = () => ({
    w: window.innerWidth,
    h: window.innerHeight
  });

  const readTrainingState = (): { w: number; h: number } | null => {
    try {
      const raw = localStorage.getItem(TRAINING_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      const w = Number(parsed.w);
      const h = Number(parsed.h);
      if (![w, h].every(n => Number.isFinite(n))) return null;
      return { w, h };
    } catch {
      return null;
    }
  };

  const writeTrainingState = () => {
    if (!trainingPanel) return;
    const rect = trainingPanel.getBoundingClientRect();
    const state = { w: rect.width, h: rect.height };
    try {
      localStorage.setItem(TRAINING_STORAGE_KEY, JSON.stringify(state));
    } catch {}
  };

  /**
   * Resize panel, anchored at bottom-left corner.
   * Panel always keeps 16:9 aspect ratio.
   */
  const applyTrainingSize = (w: number, h: number) => {
    if (!trainingPanel) return;
    const vp = getViewportBounds();
    const maxW = Math.max(TRAINING_MIN_W, vp.w - TRAINING_MARGIN * 2);
    const maxH = Math.max(TRAINING_MIN_H, vp.h - TRAINING_MARGIN * 2);
    let width = clamp(w, TRAINING_MIN_W, maxW);
    let height = width / TRAINING_ASPECT;
    if (height > maxH) {
      height = maxH;
      width = height * TRAINING_ASPECT;
    }
    if (height < TRAINING_MIN_H) {
      height = TRAINING_MIN_H;
      width = height * TRAINING_ASPECT;
    }
    trainingPanel.style.left = `${TRAINING_MARGIN}px`;
    trainingPanel.style.bottom = `${TRAINING_MARGIN}px`;
    trainingPanel.style.top = 'auto';
    trainingPanel.style.width = `${width}px`;
    trainingPanel.style.height = `${height}px`;
  };

  const setTrainingVideoLoading = (show: boolean) => {
    trainingVideoLoading?.classList.toggle('hidden', !show);
    trainingVideoLoading?.setAttribute('aria-hidden', String(!show));
  };

  trainingIframe?.addEventListener('load', () => setTrainingVideoLoading(false));

  // Prevent iframe player from capturing keyboard focus (so app hotkeys still work).
  // When the embed is fullscreen (often document.fullscreenElement === iframe), disable this
  // so Kinescope/YouTube controls and keyboard shortcuts work normally.
  if (trainingIframe && trainingPanel) {
    trainingIframe.setAttribute('tabindex', '-1');

    const getFullscreenElement = (): Element | null => {
      const d = document as Document & {
        webkitFullscreenElement?: Element | null;
        mozFullScreenElement?: Element | null;
        msFullscreenElement?: Element | null;
      };
      return (
        document.fullscreenElement ??
        d.webkitFullscreenElement ??
        d.mozFullScreenElement ??
        d.msFullscreenElement ??
        null
      );
    };

    /** True when fullscreen involves our training panel or the video iframe (typical for cross-origin embed fullscreen). */
    const isTrainingVideoFullscreen = (): boolean => {
      const el = getFullscreenElement();
      if (!el) return false;
      return el === trainingIframe || el === trainingPanel || trainingPanel.contains(el);
    };

    let trainingFullscreenActive = false;
    const syncTrainingFullscreen = () => {
      trainingFullscreenActive = isTrainingVideoFullscreen();
    };

    document.addEventListener('fullscreenchange', syncTrainingFullscreen);
    document.addEventListener('webkitfullscreenchange', syncTrainingFullscreen as EventListener);

    // Monitor focus globally - if iframe gets it, immediately blur it (unless fullscreen)
    const preventIframeFocus = () => {
      if (!trainingPanel || trainingPanel.classList.contains('hidden')) return;
      if (trainingFullscreenActive) return;
      if (document.activeElement === trainingIframe) {
        trainingIframe.blur();
      }
    };

    // Capture phase - before iframe can process focus
    document.addEventListener('focusin', preventIframeFocus, true);

    // Also blur after any click on video wrapper (player tries to focus on interaction)
    const videoWrap = trainingIframe.closest('.fabrica-training-video-wrap') as HTMLElement | null;
    videoWrap?.addEventListener('click', () => {
      setTimeout(preventIframeFocus, 0);
      setTimeout(preventIframeFocus, 100);
    });

    // Periodic check so iframe doesn't hold focus during play/pause (not in fullscreen)
    let focusWatchInterval: number | undefined;
    const startFocusWatch = () => {
      if (focusWatchInterval) return;
      syncTrainingFullscreen();
      focusWatchInterval = window.setInterval(() => {
        syncTrainingFullscreen();
        preventIframeFocus();
      }, 200);
    };
    const stopFocusWatch = () => {
      if (focusWatchInterval) {
        clearInterval(focusWatchInterval);
        focusWatchInterval = undefined;
      }
      trainingFullscreenActive = false;
    };

    window.addEventListener('showTrainingPanel', () => startFocusWatch());
    window.addEventListener('hideTrainingPanel', () => stopFocusWatch());
  }

  const stopTrainingVideo = () => {
    if (!trainingIframe) return;
    setTrainingVideoLoading(false);
    trainingIframe.src = '';
  };

  const setTrainingView = (view: 'grid' | 'player') => {
    trainingGrid?.classList.toggle('hidden', view !== 'grid');
    trainingPlayer?.classList.toggle('hidden', view !== 'player');
    trainingBackBtn?.classList.toggle('hidden', view !== 'player');
    if (view === 'grid') {
      stopTrainingVideo();
    }
  };

  const renderTrainingGrid = () => {
    if (!trainingGrid) return;
    trainingGrid.innerHTML = TRAINING_LESSONS.map(lesson => `
      <button type="button" class="fabrica-training-card" data-training-id="${lesson.id}">
        <div class="fabrica-training-thumb" style="background-image: url('${lesson.thumbnailUrl}')">
          <div class="fabrica-training-thumb-overlay">
            <div class="fabrica-training-play-badge">▶</div>
          </div>
        </div>
        <div class="fabrica-training-card-title">${lesson.title}</div>
      </button>
    `).join('');
  };

  const openTrainingLesson = (lessonId: string) => {
    const lesson = TRAINING_LESSONS.find(l => l.id === lessonId);
    if (!lesson) return;
    setTrainingView('player');
    setTrainingVideoLoading(true);
    if (trainingIframe) {
      trainingIframe.src = lesson.embedUrl;
    } else {
      setTrainingVideoLoading(false);
    }
  };

  const openTrainingPanel = (opts?: { lessonId?: string }) => {
    if (!trainingPanel) return;
    // Ensure initial placement
    const saved = readTrainingState();
    if (saved) {
      applyTrainingSize(saved.w, saved.h);
    } else {
      // Default: bottom-left, 16:9
      const w = 420;
      applyTrainingSize(w, w / (16 / 9));
      writeTrainingState();
    }

    trainingPanel.classList.remove('hidden');
    renderTrainingGrid();
    setTrainingView('grid');
    if (opts?.lessonId) {
      openTrainingLesson(opts.lessonId);
    }
  };

  const closeTrainingPanel = () => {
    trainingPanel?.classList.add('hidden');
    stopTrainingVideo();
    setTrainingView('grid');
  };

  trainingCloseBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    closeTrainingPanel();
  });

  trainingBackBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    setTrainingView('grid');
  });

  trainingGrid?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    const card = target?.closest?.('.fabrica-training-card');
    const id = card?.getAttribute('data-training-id');
    if (id) {
      e.preventDefault();
      e.stopPropagation();
      openTrainingLesson(id);
    }
  });

  // Open/Close via global events
  window.addEventListener('showTrainingPanel', ((e: Event) => {
    const detail = (e as CustomEvent).detail as any;
    openTrainingPanel({ lessonId: typeof detail?.lessonId === 'string' ? detail.lessonId : undefined });
  }) as EventListener);
  window.addEventListener('hideTrainingPanel', (() => closeTrainingPanel()) as EventListener);

  // Escape closes training panel
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    if (trainingPanel && !trainingPanel.classList.contains('hidden')) {
      closeTrainingPanel();
    }
  });

  // Resize (bottom-right handle)
  if (trainingPanel && trainingResizeHandle) {
    let resizing = false;
    let startX = 0;
    let startY = 0;
    let startW = 0;
    let startH = 0;

    const onResizeMove = (ev: PointerEvent) => {
      if (!resizing || !trainingPanel) return;
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      // Anchor at bottom-left: width grows with dx, height grows when dragging up (dy negative)
      applyTrainingSize(startW + dx, startH - dy);
    };

    const onResizeUp = (ev: PointerEvent) => {
      if (!resizing) return;
      resizing = false;
      try { trainingResizeHandle.releasePointerCapture(ev.pointerId); } catch {}
      document.body.style.userSelect = '';
      writeTrainingState();
    };

    trainingResizeHandle.addEventListener('pointerdown', (e: PointerEvent) => {
      if (!trainingPanel || trainingPanel.classList.contains('hidden')) return;
      e.preventDefault();
      e.stopPropagation();
      resizing = true;
      const rect = trainingPanel.getBoundingClientRect();
      startX = e.clientX;
      startY = e.clientY;
      startW = rect.width;
      startH = rect.height;
      document.body.style.userSelect = 'none';
      try { trainingResizeHandle.setPointerCapture(e.pointerId); } catch {}
    });

    trainingResizeHandle.addEventListener('pointermove', onResizeMove);
    trainingResizeHandle.addEventListener('pointerup', onResizeUp);
    trainingResizeHandle.addEventListener('pointercancel', onResizeUp);
    trainingResizeHandle.addEventListener('lostpointercapture', () => {
      if (!resizing) return;
      resizing = false;
      document.body.style.userSelect = '';
      writeTrainingState();
    });
  }

  // Keep panel inside viewport on resize
  window.addEventListener('resize', () => {
    if (!trainingPanel || trainingPanel.classList.contains('hidden')) return;
    const rect = trainingPanel.getBoundingClientRect();
    applyTrainingSize(rect.width, rect.height);
    writeTrainingState();
  });

  // Detect OS and update modifier keys (Cmd on Mac, Ctrl on Windows/Linux)
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const updateModifierKeys = () => {
    const modifierKeys = document.querySelectorAll('.fabrica-key-modifier');
    modifierKeys.forEach(key => {
      if (isMac) {
        key.textContent = 'Cmd';
      } else {
        key.textContent = 'Ctrl';
      }
    });
  };

  // Update modifier keys when settings panel opens
  window.addEventListener('showSettingsPanel', () => {
    setTimeout(updateModifierKeys, 10);
  });

  // Initialize draggable for settings panel (like debug-panel)
  const settingsDragHandle = settingsPanel?.querySelector('.settings-panel-drag-handle');
  if (settingsPanel && settingsDragHandle) {
    let isDragging = false;
    let offsetX = 0;
    let offsetY = 0;
    
    settingsDragHandle.addEventListener('mousedown', (e: Event) => {
      const mouseEvent = e as MouseEvent;
      // Ignore clicks on close button
      if ((mouseEvent.target as HTMLElement).tagName === 'BUTTON') return;
      
      isDragging = true;
      const rect = settingsPanel.getBoundingClientRect();
      offsetX = mouseEvent.clientX - rect.left;
      offsetY = mouseEvent.clientY - rect.top;
      
      // Reset transform when starting drag
      settingsPanel.style.transform = 'none';
      settingsPanel.style.left = `${rect.left}px`;
      settingsPanel.style.top = `${rect.top}px`;
      settingsPanel.style.right = 'auto';
      
      document.body.style.userSelect = 'none';
    });
    
    document.addEventListener('mousemove', (e: MouseEvent) => {
      if (!isDragging) return;
      
      const newX = e.clientX - offsetX;
      const newY = e.clientY - offsetY;
      
      // Constrain movement within window
      const maxX = window.innerWidth - settingsPanel.offsetWidth;
      const maxY = window.innerHeight - settingsPanel.offsetHeight;
      
      settingsPanel.style.left = `${Math.max(0, Math.min(newX, maxX))}px`;
      settingsPanel.style.top = `${Math.max(0, Math.min(newY, maxY))}px`;
    });
    
    document.addEventListener('mouseup', () => {
      if (isDragging) {
        isDragging = false;
        document.body.style.userSelect = '';
      }
    });
  }

  // Settings: rotation mode toggle (scene vs model)
  const rotationSceneBtn = document.getElementById('rotation-mode-scene') as HTMLButtonElement | null;
  const rotationModelBtn = document.getElementById('rotation-mode-model') as HTMLButtonElement | null;
  const applyRotationModeUi = (mode: 'scene' | 'model') => {
    rotationSceneBtn?.classList.toggle('active', mode === 'scene');
    rotationModelBtn?.classList.toggle('active', mode === 'model');
  };
  const savedRotationMode = (localStorage.getItem('fabrica.rotationControlMode') as 'scene' | 'model' | null) || 'model';
  applyRotationModeUi(savedRotationMode);

  rotationSceneBtn?.addEventListener('click', () => {
    const mode: 'scene' = 'scene';
    localStorage.setItem('fabrica.rotationControlMode', mode);
    applyRotationModeUi(mode);
    window.dispatchEvent(new CustomEvent('setRotationControlMode', { detail: { mode } }));
  });
  rotationModelBtn?.addEventListener('click', () => {
    const mode: 'model' = 'model';
    localStorage.setItem('fabrica.rotationControlMode', mode);
    applyRotationModeUi(mode);
    window.dispatchEvent(new CustomEvent('setRotationControlMode', { detail: { mode } }));
  });

  // Settings: simplified UI toggle -> show quick angle buttons near mini preview
  const applySimplifiedUi = (enabled: boolean) => {
    quickAngles?.classList.toggle('hidden', !enabled);
    if (simplifiedToggle) simplifiedToggle.checked = enabled;
  };
  const savedSimplified = (localStorage.getItem('fabrica.simplifiedUI') || '0') === '1';
  applySimplifiedUi(savedSimplified);

  simplifiedToggle?.addEventListener('change', () => {
    const enabled = Boolean(simplifiedToggle.checked);
    localStorage.setItem('fabrica.simplifiedUI', enabled ? '1' : '0');
    applySimplifiedUi(enabled);
  });

  // Settings: language selector (dropdown)
  const applyLanguageUi = (lang: string) => {
    if (!languageSelect) return;
    languageSelect.value = lang;
  };
  applyLanguageUi(i18n.language);
  languageSelect?.addEventListener('change', () => {
    const lang = (languageSelect.value === 'en' ? 'en' : 'ru') as 'ru' | 'en';
    window.dispatchEvent(new CustomEvent('setLanguage', { detail: lang }));
  });
  i18n.onChange(() => {
    applyLanguageUi(i18n.language);
  });

  // Quick angles buttons
  document.querySelectorAll('#quick-angles-panel .quick-angle-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const angle = (btn as HTMLElement).getAttribute('data-angle');
      if (!angle) return;
      window.dispatchEvent(new CustomEvent('setModelAngle', { detail: { angle: parseInt(angle, 10) } }));
    });
  });

  const toolbarUnified = document.querySelector('.toolbar-unified');
  const cinemaModeBtn = document.getElementById('cinema-mode-btn');

  cinemaModeBtn?.addEventListener('click', () => {
    const isActivating = !toolbarUnified?.classList.contains('cinema-active');

    if (isActivating) {
      if (toolbarUnified?.classList.contains('photo-active')) {
        window.dispatchEvent(new CustomEvent('resetModelAngle'));
        document.querySelectorAll('.photo-angle-btn').forEach(b => b.classList.remove('active'));
      }
      toolbarUnified?.classList.remove('photo-active');

      window.dispatchEvent(new CustomEvent('stopModelRotation'));
    } else {
      window.dispatchEvent(new CustomEvent('stopCameraAnimation'));
    }

    toolbarUnified?.classList.toggle('cinema-active');
  });

  document.getElementById('reset-camera-btn')?.addEventListener('click', (e) => {
    const isShowcaseActive = document.getElementById('toggle-model-rotation-btn')?.classList.contains('active');
    if (isShowcaseActive) {
      e.preventDefault();
      // UX: по Reset в витрине — сначала останавливаем витрину, затем делаем нормальный reset
      window.dispatchEvent(new CustomEvent('stopModelRotation'));
    }
    window.dispatchEvent(new CustomEvent('resetCamera'));
  });

  const screenshotBtn = document.getElementById('screenshot-btn-toolbar');
  screenshotBtn?.addEventListener('click', () => {
    const isActivating = !toolbarUnified?.classList.contains('photo-active');

    if (isActivating) {
      toolbarUnified?.classList.remove('cinema-active');
      window.dispatchEvent(new CustomEvent('stopModelRotation'));
    } else {
      window.dispatchEvent(new CustomEvent('resetModelAngle'));

      document.querySelectorAll('.photo-angle-btn').forEach(b => b.classList.remove('active'));
    }

    toolbarUnified?.classList.toggle('photo-active');
  });

  document.querySelectorAll('.photo-angle-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const angle = btn.getAttribute('data-angle');

      document.querySelectorAll('.photo-angle-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      window.dispatchEvent(new CustomEvent('setModelAngle', { detail: { angle: parseInt(angle || '1') } }));
    });
  });

  document.addEventListener('keydown', (e: KeyboardEvent) => {
    // Блокируем цифры только если пользователь редактирует текст (не ползунки)
    const target = e.target as HTMLElement;
    if (target) {
      // Блокируем только для текстовых input'ов (не range) и textarea
      if (target instanceof HTMLTextAreaElement) {
        return;
      }
      if (target instanceof HTMLInputElement) {
        // Разрешаем цифры для ползунков (type="range")
        if (target.type === 'range') {
          // Ползунки не блокируют цифры
        } else if (target.hasAttribute('data-coloris')) {
          // Цветовой пикер (Coloris): ввод с клавиатуры не нужен, всегда работают горячие клавиши (0–8)
        } else if (target.classList.contains('print-layer-name-input')) {
          // Блокируем только при редактировании названия слоя
          return;
        } else {
          // Для остальных текстовых input'ов блокируем
          return;
        }
      }
    }

    // Витрина (автоповорот модели): блокируем смену ракурсов с клавиатуры
    // чтобы не сбивалось начальное центровое положение.
    const isShowcaseActive = document.getElementById('toggle-model-rotation-btn')?.classList.contains('active');
    if (isShowcaseActive) {
      const num = parseInt(e.key);
      if (e.key === '0' || (num >= 1 && num <= 9)) {
        e.preventDefault();
        // Не спамим сообщение при удержании/быстрых нажатиях
        const now = Date.now();
        const last = (window as any).__showcaseMsgAt || 0;
        if (now - last > 800) {
          (window as any).__showcaseMsgAt = now;
          window.dispatchEvent(new CustomEvent('showToolbarMessage', {
            detail: { text: 'В режиме Витрины ракурсы не меняются', duration: 2000 }
          }));
        }
        return;
      }
    }

    if (e.key === '0') {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('resetCamera'));
      return;
    }

    const key = parseInt(e.key);
    if (key >= 1 && key <= 8) {
      e.preventDefault();

      const toolbar = document.querySelector('.toolbar-unified');
      if (toolbar?.classList.contains('photo-active')) {
        document.querySelectorAll('.photo-angle-btn').forEach(b => {
          b.classList.remove('active');
          if (b.getAttribute('data-angle') === e.key) {
            b.classList.add('active');
          }
        });
      }

      window.dispatchEvent(new CustomEvent('setModelAngle', { detail: { angle: key } }));
    }
  });

  // Mini model preview: drag rotates model by Y axis
  const modelMiniPreview = document.getElementById('model-mini-preview');
  if (modelMiniPreview) {
    let dragging = false;
    let lastX = 0;
    const sensitivityY = 0.01;  // radians per px (horizontal drag)

    const stopShowcaseIfActive = () => {
      const isShowcaseActive = document.getElementById('toggle-model-rotation-btn')?.classList.contains('active');
      if (isShowcaseActive) {
        // Важно: не сбрасываем угол — просто выключаем автоповорот
        window.dispatchEvent(new CustomEvent('stopModelRotationPreserve'));
      }
    };

    modelMiniPreview.addEventListener('pointerdown', (e: PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      stopShowcaseIfActive();
      dragging = true;
      lastX = e.clientX;
      modelMiniPreview.classList.add('active');
      try { (modelMiniPreview as any).setPointerCapture(e.pointerId); } catch {}
    });

    modelMiniPreview.addEventListener('pointermove', (e: PointerEvent) => {
      if (!dragging) return;
      e.preventDefault();
      const dx = e.clientX - lastX;
      lastX = e.clientX;

      // Вращаем только по оси Y
      const deltaY = dx * sensitivityY;
      if (deltaY) {
        window.dispatchEvent(new CustomEvent('rotateModelBy', { detail: { deltaY } }));
      }
    });

    const endDrag = (e?: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      modelMiniPreview.classList.remove('active');
      if (e) {
        try { (modelMiniPreview as any).releasePointerCapture(e.pointerId); } catch {}
      }
    };

    modelMiniPreview.addEventListener('pointerup', (e) => endDrag(e));
    modelMiniPreview.addEventListener('pointercancel', (e) => endDrag(e));
    modelMiniPreview.addEventListener('lostpointercapture', () => endDrag());
  }

  const aspectRatioSelect = document.getElementById('aspect-ratio-select') as HTMLSelectElement;
  const aspectRatioOverlay = document.getElementById('aspect-ratio-overlay');
  const aspectRatioFrame = aspectRatioOverlay?.querySelector('.aspect-ratio-frame') as HTMLElement;

  const updateAspectRatioOverlay = (): void => {
    const aspectRatio = aspectRatioSelect?.value || 'free';
    const toolbar = document.querySelector('.toolbar-unified');
    const isActive = toolbar?.classList.contains('photo-active') || toolbar?.classList.contains('cinema-active');

    if (aspectRatio === 'free' || !isActive) {
      aspectRatioOverlay?.classList.add('hidden');
      return;
    }

    aspectRatioOverlay?.classList.remove('hidden');

    const containerWidth = window.innerWidth - 233;
    const containerHeight = window.innerHeight;

    const aspectRatios: Record<string, number> = {
      '1:1': 1,
      '16:9': 16 / 9,
      '9:16': 9 / 16
    };

    const targetAspect = aspectRatios[aspectRatio];
    if (!targetAspect || !aspectRatioFrame) return;

    let frameWidth: number, frameHeight: number;
    const currentAspect = containerWidth / containerHeight;

    if (currentAspect > targetAspect) {
      frameHeight = containerHeight;
      frameWidth = containerHeight * targetAspect;
    } else {
      frameWidth = containerWidth;
      frameHeight = containerWidth / targetAspect;
    }

    aspectRatioFrame.style.width = frameWidth + 'px';
    aspectRatioFrame.style.height = frameHeight + 'px';
  };

  aspectRatioSelect?.addEventListener('change', updateAspectRatioOverlay);

  window.addEventListener('aspectRatioChange', () => {
    updateAspectRatioOverlay();
  });
  window.addEventListener('resize', updateAspectRatioOverlay);

  const updateOverlayOnModeChange = (): void => {
    setTimeout(updateAspectRatioOverlay, 50);
  };

  screenshotBtn?.addEventListener('click', updateOverlayOnModeChange);
  cinemaModeBtn?.addEventListener('click', updateOverlayOnModeChange);

  // Photo export mode toggle (one/batch) like cinema-modes
  const onePhotoExportBtn = document.getElementById('one-photo-export-btn');
  const batchExportBtn = document.getElementById('batch-export-btn');
  const setPhotoExportMode = (mode: 'one' | 'batch') => {
    const toolbar = document.querySelector('.toolbar-unified');
    if (toolbar) toolbar.setAttribute('data-photo-export-mode', mode);
    onePhotoExportBtn?.classList.toggle('active', mode === 'one');
    batchExportBtn?.classList.toggle('active', mode === 'batch');
  };
  // Default
  setPhotoExportMode('one');
  onePhotoExportBtn?.addEventListener('click', () => setPhotoExportMode('one'));
  batchExportBtn?.addEventListener('click', () => setPhotoExportMode('batch'));

  document.getElementById('unified-export-btn')?.addEventListener('click', () => {
    const toolbar = document.querySelector('.toolbar-unified');
    const aspectRatio = aspectRatioSelect?.value || 'free';

    if (toolbar?.classList.contains('cinema-active')) {
      window.dispatchEvent(new CustomEvent('exportVideo', { detail: { aspectRatio } }));
    } else if (toolbar?.classList.contains('photo-active')) {
      const mode = (toolbar.getAttribute('data-photo-export-mode') || 'one') as 'one' | 'batch';
      if (mode === 'batch') {
        window.dispatchEvent(new CustomEvent('exportPhotoBatch', { detail: { aspectRatio } }));
      } else {
        window.dispatchEvent(new CustomEvent('takeScreenshot', { detail: { aspectRatio } }));
      }
    }
  });

  const photoFovSlider = document.getElementById('photo-fov-slider') as HTMLInputElement;
  const photoFovValue = document.getElementById('photo-fov-value');

  const updatePhotoFovSliderFill = (slider: HTMLInputElement | null): void => {
    if (!slider) return;
    const min = parseFloat(slider.min) || 20;
    const max = parseFloat(slider.max) || 90;
    const val = parseFloat(slider.value);
    const percent = ((val - min) / (max - min)) * 100;
    slider.style.setProperty('--slider-value', percent + '%');
  };

  if (photoFovSlider) {
    updatePhotoFovSliderFill(photoFovSlider);
  }

  photoFovSlider?.addEventListener('input', () => {
    const fov = parseInt(photoFovSlider.value);
    if (photoFovValue) photoFovValue.textContent = fov.toString();
    updatePhotoFovSliderFill(photoFovSlider);
    window.dispatchEvent(new CustomEvent('cameraFovChange', { detail: fov }));

    const cinemaFovSlider = document.getElementById('cinema-fov-slider') as HTMLInputElement;
    const cinemaFovValue = document.getElementById('cinema-fov-value');
    if (cinemaFovSlider) {
      cinemaFovSlider.value = fov.toString();
      const min = parseFloat(cinemaFovSlider.min) || 20;
      const max = parseFloat(cinemaFovSlider.max) || 90;
      const percent = ((fov - min) / (max - min)) * 100;
      cinemaFovSlider.style.setProperty('--slider-value', percent + '%');
    }
    if (cinemaFovValue) cinemaFovValue.textContent = fov.toString();
  });

  const toggleModelRotationBtn = document.getElementById('toggle-model-rotation-btn');
  if (toggleModelRotationBtn) {
    toggleModelRotationBtn.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('toggleModelRotation'));
    });
  }

  const arcShortBtn = document.getElementById('arc-short-btn');
  const arcLongBtn = document.getElementById('arc-long-btn');
  const turntableBtn = document.getElementById('turntable-btn');
  const camPoseStartBtn = document.getElementById('cam-pose-start') as HTMLButtonElement;
  const camPoseEndBtn = document.getElementById('cam-pose-end') as HTMLButtonElement;
  const startIndicator = document.getElementById('start-indicator');
  const endIndicator = document.getElementById('end-indicator');

  camPoseStartBtn?.addEventListener('click', () => {
    window.dispatchEvent(new CustomEvent('stopModelRotation'));
    window.dispatchEvent(new CustomEvent('setCameraStart'));
    startIndicator?.classList.remove('orange', 'gray');
    startIndicator?.classList.add('green');
  });

  camPoseEndBtn?.addEventListener('click', () => {
    window.dispatchEvent(new CustomEvent('stopModelRotation'));
    window.dispatchEvent(new CustomEvent('setCameraEnd'));
    endIndicator?.classList.remove('orange', 'gray');
    endIndicator?.classList.add('green');
  });

  const setArcMode = (mode: 'short' | 'long' | 'turntable'): void => {
    arcShortBtn?.classList.remove('active');
    arcLongBtn?.classList.remove('active');
    turntableBtn?.classList.remove('active');

    if (mode === 'short') {
      arcShortBtn?.classList.add('active');
    } else if (mode === 'long') {
      arcLongBtn?.classList.add('active');
    } else if (mode === 'turntable') {
      turntableBtn?.classList.add('active');
    }

    const isTurntable = mode === 'turntable';
    if (camPoseStartBtn) camPoseStartBtn.disabled = isTurntable;
    if (camPoseEndBtn) camPoseEndBtn.disabled = isTurntable;

    if (startIndicator) {
      startIndicator.classList.toggle('gray', isTurntable);
      if (isTurntable) {
        startIndicator.classList.remove('orange', 'green');
      } else if (!startIndicator.classList.contains('green')) {
        startIndicator.classList.add('orange');
      }
    }
    if (endIndicator) {
      endIndicator.classList.toggle('gray', isTurntable);
      if (isTurntable) {
        endIndicator.classList.remove('orange', 'green');
      } else if (!endIndicator.classList.contains('green')) {
        endIndicator.classList.add('orange');
      }
    }

    window.dispatchEvent(new CustomEvent('setArcMode', { detail: mode }));
  };

  arcShortBtn?.addEventListener('click', () => setArcMode('short'));
  arcLongBtn?.addEventListener('click', () => setArcMode('long'));
  turntableBtn?.addEventListener('click', () => setArcMode('turntable'));

  const animDurationInput = document.getElementById('anim-duration') as HTMLInputElement;
  
  // Ограничиваем ввод только двумя цифрами
  animDurationInput?.addEventListener('input', () => {
    let value = animDurationInput.value.replace(/\D/g, ''); // Убираем все нецифровые символы
    if (value.length > 2) {
      value = value.slice(0, 2); // Ограничиваем двумя цифрами
    }
    animDurationInput.value = value;
  });
  
  animDurationInput?.addEventListener('change', () => {
    let duration = parseInt(animDurationInput.value) || 6;
    
    // Ограничиваем максимум 20 секундами
    if (duration > 20) {
      duration = 20;
      animDurationInput.value = '20';
      window.dispatchEvent(new CustomEvent('showToolbarMessage', { 
        detail: { text: 'Максимальное время анимации — 20 секунд', duration: 3000 } 
      }));
    }
    
    // Минимум 1 секунда
    if (duration < 1) {
      duration = 1;
      animDurationInput.value = '1';
    }
    
    window.dispatchEvent(new CustomEvent('setAnimDuration', { detail: duration * 1000 }));
  });

  const cinemaFovSlider = document.getElementById('cinema-fov-slider') as HTMLInputElement;
  const cinemaFovValue = document.getElementById('cinema-fov-value');

  const updateFovSliderFill = (slider: HTMLInputElement | null): void => {
    if (!slider) return;
    const min = parseFloat(slider.min) || 20;
    const max = parseFloat(slider.max) || 90;
    const val = parseFloat(slider.value);
    const percent = ((val - min) / (max - min)) * 100;
    slider.style.setProperty('--slider-value', percent + '%');
  };

  updateFovSliderFill(cinemaFovSlider);

  cinemaFovSlider?.addEventListener('input', () => {
    const val = cinemaFovSlider.value;
    if (cinemaFovValue) cinemaFovValue.textContent = val;
    updateFovSliderFill(cinemaFovSlider);
    window.dispatchEvent(new CustomEvent('cameraFovChange', { detail: parseFloat(val) }));

    const photoFovSlider = document.getElementById('photo-fov-slider') as HTMLInputElement;
    const photoFovValue = document.getElementById('photo-fov-value');
    if (photoFovSlider) {
      photoFovSlider.value = val;
      const min = parseFloat(photoFovSlider.min) || 20;
      const max = parseFloat(photoFovSlider.max) || 90;
      const percent = ((parseFloat(val) - min) / (max - min)) * 100;
      photoFovSlider.style.setProperty('--slider-value', percent + '%');
    }
    if (photoFovValue) photoFovValue.textContent = val;
  });

  document.getElementById('cam-goto-start')?.addEventListener('click', () => {
    window.dispatchEvent(new CustomEvent('gotoStart'));
  });

  const playBtn = document.getElementById('cam-pose-play');
  let isPlaying = false;
  let isStopping = false; // Флаг для предотвращения повторного запуска
  
  const playIconSvg = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.633 2.322c-.802-.721-2.078-.152-2.078.926v16.628c0 1.042 1.203 1.623 2.019.976l9.826-7.795a1.245 1.245 0 0 0 .058-1.902z" fill="#555"/>
  </svg>`;
  
  const stopIconSvg = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="5" width="14" height="14" rx="2" fill="#555"/>
  </svg>`;
  
  const updatePlayButton = (playing: boolean) => {
    if (isStopping && playing) {
      // Игнорируем animationStarted если мы только что остановили
      return;
    }
    isPlaying = playing;
    isStopping = false;
    if (playBtn) {
      playBtn.innerHTML = playing ? stopIconSvg : playIconSvg;
      playBtn.title = playing ? t('toolbar.stop') : t('toolbar.play');
    }
  };
  
  playBtn?.addEventListener('click', () => {
    if (isPlaying) {
      isStopping = true;
      window.dispatchEvent(new CustomEvent('stopCameraAnimation'));
    } else {
      window.dispatchEvent(new CustomEvent('playCameraAnimation'));
    }
  });
  
  window.addEventListener('animationStarted', () => {
    updatePlayButton(true);
  });
  window.addEventListener('animationStopped', () => {
    isStopping = false; // Сбрасываем флаг при получении события остановки
    updatePlayButton(false);
  });

  document.getElementById('cam-goto-end')?.addEventListener('click', () => {
    window.dispatchEvent(new CustomEvent('gotoEnd'));
  });

  const sliders = document.querySelectorAll('.fabrica-slider');
  sliders.forEach(slider => {
    updateSliderVisualFill(slider as HTMLInputElement);

    slider.addEventListener('input', () => {
      updateSliderVisualFill(slider as HTMLInputElement);
    });
  });

  const updateContrastColors = (): void => {
    const elements = [
      document.getElementById('file-btn'),
      document.querySelector('.fabrica-logo')
    ].filter(el => el !== null) as HTMLElement[];

    if (elements.length === 0) return;

    const canvas = document.getElementById('canvas-container');
    const bgColorInput = document.getElementById('bg-color') as HTMLInputElement;
    let bgColor = '#000000';

    if (bgColorInput && bgColorInput.value) {
      bgColor = bgColorInput.value;
    } else if (canvas) {
      const computedStyle = window.getComputedStyle(canvas);
      bgColor = computedStyle.backgroundColor || '#000000';
    } else {
      const bodyStyle = window.getComputedStyle(document.body);
      bgColor = bodyStyle.backgroundColor || '#000000';
    }

    const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
      hex = hex.replace('#', '');

      if (hex.toLowerCase() === 'transparent' || (hex.length === 8 && parseInt(hex.slice(-2), 16) === 0)) {
        return { r: 0, g: 0, b: 0 };
      }

      if (hex.length === 8) {
        hex = hex.slice(0, 6);
      }

      if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('');
      }

      const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };

    const parseRgb = (rgb: string): { r: number; g: number; b: number } | null => {
      const match = rgb.match(/\d+/g);
      if (match && match.length >= 3) {
        return {
          r: parseInt(match[0]),
          g: parseInt(match[1]),
          b: parseInt(match[2])
        };
      }
      return null;
    };

    let rgb: { r: number; g: number; b: number } | null = null;
    if (bgColor.startsWith('#')) {
      rgb = hexToRgb(bgColor);
    } else if (bgColor.startsWith('rgb')) {
      rgb = parseRgb(bgColor);
    }

    if (!rgb) {
      elements.forEach(el => el.style.color = '#FFFFFF');
      return;
    }

    const luminance = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;

    const textColor = luminance > 0.5 ? '#000000' : '#FFFFFF';
    elements.forEach(el => el.style.color = textColor);
  };

  updateContrastColors();
}

